import React, { useEffect, useMemo, useState } from 'react';
import { InputNumber } from 'antd';
import type { FilterDropdownProps, FilterValue } from 'antd/es/table/interface';
import { useMutation, useNavigate, useParams, useQuery } from '@common/hooks';
import {
  AppContent,
  Banner,
  Button,
  EmptyContent,
  ErrorHandler,
  FilteredTable,
  FullScreenLoadingGrid,
  Gap,
  Gutter,
  Input,
  Modal,
  MultiSelectTableFilter,
  StatusPill,
  Tag,
  Text,
  Title,
  WorksAvatar,
} from '@common/components';
import { datetime, todayIso } from '@common/services';
import { buildDialogUrl } from '@common/services/dialogs-service';
import { useSetBreadcrumbs } from 'works/legacy/lib/client-store';
import { createMessage } from 'works/legacy/lib/message-helper';
import { SERVICE_LINE_WORKERS_QUERY } from '@load-balancing/graphql/queries/service-line-workers';
import { SERVICE_LINES_QUERY } from '@load-balancing/graphql/queries/service-lines';
import { SERVICE_LINE_TEAM_FILTERS_QUERY } from '@load-balancing/graphql/queries/service-line-team-filters';
import { SERVICE_LINE_SCHEDULING_PERIOD_QUERY } from '@load-balancing/graphql/queries/service-line-scheduling-period';
import { REGENERATE_SCHEDULE_RUN_MUTATION } from '@load-balancing/graphql/mutations/regenerate-schedule-run';
import { useScheduleGenerationLoader } from '@load-balancing/hooks';
import {
  employmentLabel,
  fteHoursLabel,
  shiftPatternLabel,
  shiftPreferenceLabel,
  toTeamReviewWorker,
  type TeamReviewWorker,
} from '@load-balancing/services';
import {
  EMPLOYMENT_FILTER_OPTIONS,
  FTE_HOURS_MAX_DEFAULT,
  FTE_HOURS_MIN,
  SCHEDULE_GENERATION_FAILED_MESSAGE,
  SCHEDULE_GENERATION_LOADER_TITLE,
  SCHEDULE_GENERATION_SLOW_MESSAGE,
  SCHEDULE_GENERATION_STEP_INTERVAL_MS,
  SCHEDULE_GENERATION_STEPS,
  SHIFT_PATTERN_FILTER_OPTIONS,
  SHIFT_PREFERENCE_FILTER_OPTIONS,
  STATUS_FILTER_OPTIONS,
} from './_schedule-team-review-page-constants';
import {
  generateSchedulesModalSummary,
  generateSchedulesModalTitle,
  regenerateErrorMessage,
} from './_schedule-team-review-page-service';

function normalizeNameCase(name: string): string {
  if (name === name.toUpperCase()) {
    return name.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return name;
}

function nameToFirstLast(name: string): {
  firstName: string;
  lastName: string;
} {
  const idx = name.indexOf(' ');
  if (idx === -1) return { firstName: name, lastName: '' };
  return { firstName: name.slice(0, idx), lastName: name.slice(idx + 1) };
}

// Min/max range filter for FTE Hours. Unlike the shared NumberRangeFilter, it lets a
// single bound be set (the other defaults on apply) and accepts a 0 min — that
// component's Apply rejects both (`if (!min || !max) return`, where `!0` is true).
// Defaulting the blank side keeps filteredValue a complete [min, max] pair, so the
// `hoursRange` chip and the column's onFilter stay simple.
function FteHoursRangeFilter({
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
}: FilterDropdownProps) {
  const [minInput, setMinInput] = useState<number | null>(
    typeof selectedKeys[0] === 'number' ? selectedKeys[0] : null
  );
  const [maxInput, setMaxInput] = useState<number | null>(
    typeof selectedKeys[1] === 'number' ? selectedKeys[1] : null
  );

  function reset() {
    setMinInput(null);
    setMaxInput(null);
    clearFilters?.();
    confirm();
  }

  function apply() {
    // Applying with both sides empty is a no-op clear, not a 0–240 filter that would
    // hide the "—" (null FTE) rows.
    if (minInput === null && maxInput === null) {
      reset();
      return;
    }
    setSelectedKeys([
      minInput ?? FTE_HOURS_MIN,
      maxInput ?? FTE_HOURS_MAX_DEFAULT,
    ]);
    confirm();
  }

  return (
    <Gutter size="sm" asPadding>
      <Gap align="center" size="sm">
        <InputNumber
          autoFocus
          min={FTE_HOURS_MIN}
          placeholder="Min"
          style={{ width: 80 }}
          value={minInput}
          onChange={(value) => setMinInput(value as number | null)}
        />
        <Text secondary>–</Text>
        <InputNumber
          min={FTE_HOURS_MIN}
          placeholder="Max"
          style={{ width: 80 }}
          value={maxInput}
          onChange={(value) => setMaxInput(value as number | null)}
        />
      </Gap>
      <Gutter direction="top" size="sm">
        <Gap justify="space-between" align="center">
          <Button kind="link" size="sm" onClick={reset}>
            Reset
          </Button>
          <Button kind="primary" size="sm" onClick={apply}>
            Apply
          </Button>
        </Gap>
      </Gutter>
    </Gutter>
  );
}

export function ScheduleTeamReviewPage() {
  // The page is scoped to a (service line, scheduling period) — both are path
  // segments. The period is structural: it drives FTE hours and anchors the roster's
  // "current term" selection on the period's start date.
  const { serviceLineId, schedulingPeriodId } = useParams<{
    serviceLineId: string;
    schedulingPeriodId: string;
  }>();
  const navigate = useNavigate();

  // "Confirm & Generate Schedules" opens a confirmation modal; confirming it rejects the
  // period's in-review run and starts a fresh greedy run. On success we return to the
  // dashboard (where the new run surfaces); on error we close the modal and surface
  // per-code copy (see _schedule-team-review-page-service).
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // The just-triggered run the full-screen loader is tracking. Set on a successful regenerate;
  // the loader stays up (and blocks a re-fire) until the run is reviewable, fails, or the slow
  // timeout elapses. Null when no generation is in flight.
  const [generatingRunId, setGeneratingRunId] = useState<string | null>(null);
  const [regenerate, { loading: isGenerating }] = useMutation(
    REGENERATE_SCHEDULE_RUN_MUTATION,
    {
      // Don't navigate yet: close the modal and hand the new run to the loader, which polls
      // until it's reviewable and only then navigates to the dashboard.
      onCompleted: ({ regenerateScheduleRun }) => {
        setShowConfirmModal(false);
        if (regenerateScheduleRun) {
          setGeneratingRunId(regenerateScheduleRun.balancerRunId);
        }
      },
      onError: (error) => {
        setShowConfirmModal(false);
        createMessage('error', regenerateErrorMessage(error));
      },
    }
  );

  const dashboardPath = '/works/schedule/dashboard';

  // Pure poll + state machine over the new run's status; the page performs the side effects.
  const { phase: generationPhase, open: loaderOpen } =
    useScheduleGenerationLoader({
      balancerRunId: generatingRunId,
      serviceLineId,
    });

  // `done` navigates via the loader's onComplete (after its completed beat); `failed` toasts and
  // stays put for retry; `slow` informs and auto-navigates (the run keeps generating server-side).
  useEffect(() => {
    if (generationPhase === 'failed') {
      createMessage('error', SCHEDULE_GENERATION_FAILED_MESSAGE);
      setGeneratingRunId(null);
    } else if (generationPhase === 'slow') {
      createMessage('error', SCHEDULE_GENERATION_SLOW_MESSAGE);
      setGeneratingRunId(null);
      navigate(dashboardPath);
    }
  }, [generationPhase, navigate, dashboardPath]);

  // Controlled column filters. Each filterable column reads its `filteredValue`
  // from here, and `onFilterChange` writes back — so the filter-chips row and the
  // actual column filtering share one source of truth. Without this, removing a
  // chip would update the chips row but leave AntD's internal filter state intact.
  const [filters, setFilters] = useState<Record<string, FilterValue | null>>({
    status: ['active'],
  });

  const { data, loading, error } = useQuery(SERVICE_LINE_WORKERS_QUERY, {
    variables: { serviceLineId: serviceLineId ?? '', schedulingPeriodId },
    skip: !serviceLineId,
  });
  const { data: serviceLinesData } = useQuery(SERVICE_LINES_QUERY);
  const { data: filtersData } = useQuery(SERVICE_LINE_TEAM_FILTERS_QUERY, {
    variables: { serviceLineId: serviceLineId ?? '' },
    skip: !serviceLineId,
  });
  const { data: periodData, loading: periodLoading } = useQuery(
    SERVICE_LINE_SCHEDULING_PERIOD_QUERY,
    {
      variables: {
        serviceLineId: serviceLineId ?? '',
        schedulingPeriodId: schedulingPeriodId ?? '',
      },
      skip: !serviceLineId || !schedulingPeriodId,
    }
  );

  const serviceLineName = serviceLinesData?.serviceLines.find(
    (line) => line.id === serviceLineId
  )?.name;

  useSetBreadcrumbs([
    { name: 'Schedule', path: '/works/schedule' },
    {
      name: 'Dashboard',
      path: '/works/schedule/dashboard',
    },
    {
      name: 'Review Team',
      path: `/works/schedule/dashboard/${serviceLineId}/team-review/${schedulingPeriodId}`,
    },
  ]);

  // The page is scoped to a real scheduling period (a required path segment); its start
  // anchors which contract term is "current". When it can't be resolved — no param, or a
  // stale / out-of-org uuid the org-scoped lookup returns null for — there is nothing
  // meaningful to anchor or display, so the page renders a not-found state (below).
  const period = periodData?.serviceLineSchedulingPeriod ?? null;
  const periodStart = period?.periodStart ?? null;
  const periodEnd = period?.periodEnd ?? null;
  const periodMissing =
    !schedulingPeriodId || (!periodLoading && period === null);

  // Once resolved, the anchor is the period start. During the brief load window it falls
  // back to today, but the roster shows a spinner then (loading is gated on periodLoading
  // below), so that placeholder anchor is never displayed.
  const asOf = periodStart ?? todayIso();

  // Rendered as a single h2: "Review Team — {service line} · {May 24 – Jul 4, 2026}".
  const periodLabel =
    periodStart && periodEnd
      ? `${datetime(periodStart).format('MMM D')} – ${datetime(
          periodEnd
        ).format('MMM D, YYYY')}`
      : null;
  const headerText = serviceLineName
    ? `Review Team — ${serviceLineName}${periodLabel ? ` · ${periodLabel}` : ''}`
    : 'Review Team';

  const displayWorkers = useMemo(
    () =>
      (data?.serviceLineWorkers ?? [])
        .map((worker) => toTeamReviewWorker(worker, asOf))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [data, asOf]
  );

  // Unit and Role filter options are the service line's configured units/roles
  // (caller-authorized), already ordered and de-duplicated server-side. The Unit filter
  // keys on the unit uuid (`value`) — unit names aren't guaranteed unique — while the
  // chip and column still show the name (`text`). Roles key on their abbreviation, which
  // is itself the unique identifier.
  const unitFilterOptions = useMemo(
    () =>
      (filtersData?.serviceLineTeamFilters?.units ?? []).map((unit) => ({
        text: unit.name,
        value: unit.id,
      })),
    [filtersData]
  );
  const roleFilterOptions = useMemo(
    () =>
      (filtersData?.serviceLineTeamFilters?.roles ?? []).map(
        (abbreviation: string) => ({
          text: abbreviation,
          value: abbreviation,
        })
      ),
    [filtersData]
  );

  function handleConfirm() {
    setShowConfirmModal(true);
  }

  function handleRunBalancer() {
    // Block a second mutation while one is in flight or the loader is still tracking a run.
    if (
      isGenerating ||
      generatingRunId !== null ||
      !serviceLineId ||
      !schedulingPeriodId
    )
      return;
    regenerate({ variables: { serviceLineId, schedulingPeriodId } });
  }

  if (error) {
    return <ErrorHandler error={error} log="ScheduleTeamReviewPage" />;
  }

  // The scheduling period is required; without a resolvable one there's no roster to show.
  if (periodMissing) {
    return (
      <AppContent>
        <EmptyContent
          header="Scheduling period not found"
          text="This page needs a valid scheduling period. Open Review Team from the schedule dashboard."
        />
      </AppContent>
    );
  }

  const columns = [
    {
      title: 'Name',
      key: 'name',
      filteredValue: filters.name ?? null,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
      }: FilterDropdownProps) => (
        <Gutter size="sm" asPadding>
          <Input
            autoFocus
            placeholder="Search name"
            value={selectedKeys[0] as string}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
          />
        </Gutter>
      ),
      onFilter: (value: string | number | boolean, record: TeamReviewWorker) =>
        record.name.toLowerCase().includes(String(value).toLowerCase()),
      render: (_: unknown, worker: TeamReviewWorker) => {
        const normalizedName = normalizeNameCase(worker.name);
        const { firstName, lastName } = nameToFirstLast(normalizedName);
        return (
          <Gap align="center">
            <WorksAvatar firstName={firstName} lastName={lastName} size={28} />
            <Button
              kind="link"
              size="sm"
              onClick={() =>
                navigate(
                  buildDialogUrl('schedule-worker-view-drawer', {
                    props: {
                      workerId: worker.id,
                      ...(serviceLineId ? { serviceLineId } : {}),
                      ...(schedulingPeriodId ? { schedulingPeriodId } : {}),
                      ...(periodStart ? { asOf: periodStart } : {}),
                    },
                  })
                )
              }
            >
              {normalizedName}
            </Button>
          </Gap>
        );
      },
    },
    {
      title: 'Employment',
      key: 'employment',
      filteredValue: filters.employment ?? null,
      filters: EMPLOYMENT_FILTER_OPTIONS,
      filterDropdown: MultiSelectTableFilter,
      onFilter: (value: string | number | boolean, record: TeamReviewWorker) =>
        record.employmentType === String(value),
      render: (_: unknown, worker: TeamReviewWorker) => (
        <Tag color="steelLighter" kind="square">
          {employmentLabel(worker.employmentType)}
        </Tag>
      ),
    },
    {
      title: 'Unit',
      key: 'unit',
      filteredValue: filters.unit ?? null,
      filters: unitFilterOptions,
      filterDropdown: MultiSelectTableFilter,
      // Match on the unit uuid (the option `value`); the cell still renders the name.
      onFilter: (value: string | number | boolean, record: TeamReviewWorker) =>
        record.unitId === String(value),
      render: (_: unknown, worker: TeamReviewWorker) => worker.unit ?? '—',
    },
    {
      title: 'Role',
      key: 'role',
      filteredValue: filters.role ?? null,
      filters: roleFilterOptions,
      filterDropdown: MultiSelectTableFilter,
      onFilter: (value: string | number | boolean, record: TeamReviewWorker) =>
        record.role === String(value),
      render: (_: unknown, worker: TeamReviewWorker) => worker.role ?? '—',
    },
    {
      title: 'Charge Eligible',
      key: 'chargeEligible',
      filteredValue: filters.chargeEligible ?? null,
      filters: [
        { text: 'Yes', value: 'yes' },
        { text: 'No', value: 'no' },
      ],
      filterDropdown: MultiSelectTableFilter,
      onFilter: (value: string | number | boolean, record: TeamReviewWorker) =>
        value === 'yes' ? record.chargeEligible : !record.chargeEligible,
      render: (_: unknown, worker: TeamReviewWorker) =>
        worker.chargeEligible ? 'Yes' : '—',
    },
    {
      title: 'FTE Hours',
      key: 'fteHours',
      filteredValue: filters.fteHours ?? null,
      filterFormat: 'hoursRange' as const,
      filterDropdown: FteHoursRangeFilter,
      // AntD invokes onFilter once per selected value; for a [min, max] range we
      // ignore the per-value arg and evaluate the whole range from state, so both
      // invocations agree. "—" rows (no FTE on the current term) fall outside it.
      onFilter: (
        _value: string | number | boolean,
        record: TeamReviewWorker
      ) => {
        const range = filters.fteHours;
        if (!Array.isArray(range) || range.length < 2) return true;
        const [min, max] = range as [number, number];
        if (record.fteHours === null) return false;
        return record.fteHours >= min && record.fteHours <= max;
      },
      render: (_: unknown, worker: TeamReviewWorker) =>
        fteHoursLabel(worker.fteHours),
    },
    {
      title: 'Shift Preference',
      key: 'shiftType',
      filteredValue: filters.shiftType ?? null,
      filters: SHIFT_PREFERENCE_FILTER_OPTIONS,
      filterDropdown: MultiSelectTableFilter,
      onFilter: (value: string | number | boolean, record: TeamReviewWorker) =>
        record.shiftType === String(value),
      render: (_: unknown, worker: TeamReviewWorker) => (
        <Text>{shiftPreferenceLabel(worker.shiftType)}</Text>
      ),
    },
    {
      title: 'Shift Pattern',
      key: 'shiftPattern',
      filteredValue: filters.shiftPattern ?? null,
      filters: SHIFT_PATTERN_FILTER_OPTIONS,
      filterDropdown: MultiSelectTableFilter,
      onFilter: (value: string | number | boolean, record: TeamReviewWorker) =>
        record.shiftPattern === String(value),
      render: (_: unknown, worker: TeamReviewWorker) => (
        <Text>{shiftPatternLabel(worker.shiftPattern)}</Text>
      ),
    },
    {
      title: 'Profile Changes',
      key: 'changes',
      filteredValue: filters.changes ?? null,
      filters: [
        { text: 'Profile Change', value: 'profile_change' },
        { text: 'No changes', value: 'no_changes' },
      ],
      filterDropdown: MultiSelectTableFilter,
      onFilter: (
        value: string | number | boolean,
        record: TeamReviewWorker
      ) => {
        if (value === 'profile_change')
          return record.upcomingChanges.length > 0;
        if (value === 'no_changes') return record.upcomingChanges.length === 0;
        return true;
      },
      render: (_: unknown, worker: TeamReviewWorker) => {
        if (worker.upcomingChanges.length > 0) {
          return (
            <Tag color="steelLighter" kind="square">
              Profile Change
            </Tag>
          );
        }
        return '—';
      },
    },
    {
      title: 'Status',
      key: 'status',
      filteredValue: filters.status ?? null,
      filters: STATUS_FILTER_OPTIONS,
      filterDropdown: MultiSelectTableFilter,
      onFilter: (value: string | number | boolean, record: TeamReviewWorker) =>
        record.status === String(value),
      render: (_: unknown, worker: TeamReviewWorker) => (
        <StatusPill
          variant={worker.status === 'active' ? 'success' : 'neutral'}
        >
          {worker.status === 'active' ? 'Active' : 'Inactive'}
        </StatusPill>
      ),
    },
  ];

  return (
    <AppContent>
      {/* ── Page header: title + CTA ── */}
      <Gutter direction="bottom" size="lg">
        <Gap align="center" justify="space-between">
          <Title size="h2">{headerText}</Title>
          <Button kind="primary" size="md" onClick={handleConfirm}>
            Confirm & Generate Schedules
          </Button>
        </Gap>
      </Gutter>

      {/* ── Info banner ── */}
      <Gutter direction="bottom" size="lg">
        <Banner type="info" showIcon>
          <Text block>
            <>
              <Text bold>Review your team before reviewing the schedule.</Text>{' '}
              Accurate roles, FTEs, and start and end dates lead
            </>
          </Text>
          <Text block>
            to better schedule results. Changes made after this won&apos;t
            generate a new balanced schedule.
          </Text>
        </Banner>
      </Gutter>

      <FilteredTable
        dataSource={displayWorkers}
        rowKey="id"
        columns={columns}
        loading={loading || periodLoading}
        filters={filters}
        onFilterChange={setFilters}
        pagination={{
          // No explicit `total`: AntD derives it from the filtered/sorted rows,
          // so page numbers track the visible result set. Passing the unfiltered
          // count here lets stale phantom pages survive client-side filtering.
          hideOnSinglePage: true,
          pageSize: 100,
        }}
        scroll={{ x: 'max-content' }}
        locale={{
          emptyText: (
            <EmptyContent
              header="No team members"
              text="There are no workers for this service line yet."
            />
          ),
        }}
      />

      <Modal
        title={generateSchedulesModalTitle(serviceLineName)}
        visible={showConfirmModal}
        okText="Confirm"
        cancelText="Cancel"
        confirmLoading={isGenerating}
        onOk={handleRunBalancer}
        onCancel={() => setShowConfirmModal(false)}
      >
        <Gap vertical size="md">
          <Text block>
            {generateSchedulesModalSummary(serviceLineName, periodLabel)}
          </Text>
          <Text block>
            Before continuing, make sure roles, FTEs, and employment dates are
            accurate. Schedule results are based on the team data you just
            reviewed.
          </Text>
        </Gap>
      </Modal>

      {/* ── Full-screen generation loader: up from `generating` through the `done`
          beat. `failed`/`slow` unmount it (handled above) so its onComplete-navigate
          never fires; on `done` the open=false flip plays the beat, then onComplete
          navigates to the dashboard. ── */}
      {(generationPhase === 'generating' || generationPhase === 'done') && (
        <FullScreenLoadingGrid
          open={loaderOpen}
          title={SCHEDULE_GENERATION_LOADER_TITLE}
          steps={SCHEDULE_GENERATION_STEPS}
          stepIntervalMs={SCHEDULE_GENERATION_STEP_INTERVAL_MS}
          onComplete={() => navigate(dashboardPath)}
        />
      )}
    </AppContent>
  );
}
